// AdminPage shell — pure tab orchestration.
//
// Each tab now lives in its own file under ./admin/. See:
//   • ./admin/_shared.jsx           — helpers, ROLE_META, Field/Modal/StatCard...
//   • ./admin/DashboardTab.jsx
//   • ./admin/UsersTab.jsx          — Super Admin only
//   • ./admin/RoleApplicationsTab.jsx
//   • ./admin/EventsTab.jsx         — orchestrates EventForm, AdminPhasesView, AdminRegistrationView
//   • ./admin/EventForm.jsx
//   • ./admin/AdminPhasesView.jsx
//   • ./admin/AdminRegistrationView.jsx
//   • ./admin/PhasePDFUpload.jsx
//   • ./admin/MapLocationPicker.jsx
//   • ./admin/ShopTab.jsx + ProductForm.jsx
//   • ./admin/RegistrationsTab.jsx
//   • ./admin/ApprovalsTab.jsx
//
// Refactored from a 3553-line monolith during iter66.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../config/axios';
import { toast } from 'sonner';

import AdminBookingsRoot from '../components/admin/AdminBookingsRoot';
import AdminBroadcastTab from '../components/admin/AdminBroadcastTab';

import DashboardTab from './admin/DashboardTab';
import UsersTab from './admin/UsersTab';
import RoleApplicationsTab from './admin/RoleApplicationsTab';
import EventsTab from './admin/EventsTab';
import ShopTab from './admin/ShopTab';
import PartnersTab from './admin/PartnersTab';
import RegistrationsTab from './admin/RegistrationsTab';
import ApprovalsTab from './admin/ApprovalsTab';
import MembershipsTab from './admin/MembershipsTab';
import AdminReviewsTab from './admin/AdminReviewsTab';

const TABS_ADMIN = [
  { id: 0, label: 'Dashboard', icon: 'ph-squares-four' },
  { id: 2, label: 'Applications', icon: 'ph-clipboard-text' },
  { id: 3, label: 'Events', icon: 'ph-calendar' },
  { id: 4, label: 'Shop', icon: 'ph-storefront' },
  { id: 9, label: 'Partners', icon: 'ph-handshake' },
  { id: 5, label: 'Registrations', icon: 'ph-list-bullets' },
  { id: 6, label: 'Approvals', icon: 'ph-check-square' },
  { id: 7, label: 'Bookings', icon: 'ph-ticket' },
  { id: 8, label: 'Broadcast', icon: 'ph-megaphone-simple' },
  { id: 11, label: 'Reviews', icon: 'ph-star' },
];
const TABS_SUPER = [
  { id: 0, label: 'Dashboard', icon: 'ph-squares-four' },
  { id: 1, label: 'Users', icon: 'ph-users-three' },
  { id: 2, label: 'Applications', icon: 'ph-clipboard-text' },
  { id: 3, label: 'Events', icon: 'ph-calendar' },
  { id: 4, label: 'Shop', icon: 'ph-storefront' },
  { id: 9, label: 'Partners', icon: 'ph-handshake' },
  { id: 10, label: 'Memberships', icon: 'ph-crown' },
  { id: 5, label: 'Registrations', icon: 'ph-list-bullets' },
  { id: 6, label: 'Approvals', icon: 'ph-check-square' },
  { id: 7, label: 'Bookings', icon: 'ph-ticket' },
  { id: 8, label: 'Broadcast', icon: 'ph-megaphone-simple' },
  { id: 11, label: 'Reviews', icon: 'ph-star' },
];

export default function AdminPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  // Switchable screens (backend/features.py) — off until the backend says otherwise
  const [features, setFeatures] = useState({ shop: false, shop_orders: false });

  useEffect(() => {
    axiosInstance.get('/admin/features').then(r => setFeatures(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    axiosInstance.get('/auth/me')
      .then(r => {
        const u = r.data;
        const roles = u.roles || [];
        if (!roles.includes('admin') && !roles.includes('super_admin')) {
          toast.error('Access denied');
          navigate('/profile');
          return;
        }
        setUser(u);
        setLoading(false);
      })
      .catch(() => { navigate('/auth'); });
  }, [navigate]);

  if (loading) return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E54562]" />
    </div>
  );

  const isSuperAdmin = (user?.roles || []).includes('super_admin');
  // The Shop tab is for Super Admins unless switched on for Admins (backend/features.py)
  const TABS = (isSuperAdmin ? TABS_SUPER : TABS_ADMIN).filter(t => t.id !== 4 || features.shop);
  const activeTab = TABS.find(t => t.id === tab) ? tab : 0;

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-nav text-[#2B3A67]" style={{ fontFamily: "'Outfit', sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-3 shadow-sm pt-safe">
        <button onClick={() => navigate('/profile')}
          className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center">
          <i className="ph-bold ph-arrow-left" />
        </button>
        <div className="flex-1">
          <h1 className="font-extrabold text-base leading-tight">
            {isSuperAdmin ? 'Super Admin Panel' : 'Admin Dashboard'}
          </h1>
          <p className="text-xs text-gray-400">{user?.name}</p>
        </div>
        <button
          data-testid="admin-checkin-btn"
          onClick={() => navigate('/admin/check-in')}
          className="px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-bold flex items-center gap-1.5 mr-2"
          aria-label="Open ticket check-in"
        >
          <i className="ph-fill ph-qr-code" />
          Check-in
        </button>
        <div className={`px-2.5 py-1 rounded-full text-xs font-bold ${isSuperAdmin ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}>
          {isSuperAdmin ? 'Super Admin' : 'Admin'}
        </div>
      </header>

      {/* Tab bar */}
      <div className="sticky top-[57px] z-20 bg-white border-b border-gray-100 overflow-x-auto">
        <div className="flex px-4 gap-0 min-w-max">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-xs font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === t.id ? 'border-[#2B3A67] text-[#2B3A67]' : 'border-transparent text-gray-400'}`}>
              <i className={`ph-bold ${t.icon}`} />{t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <main className="max-w-2xl mx-auto px-4 py-5">
        {activeTab === 0 && <DashboardTab isSuperAdmin={isSuperAdmin} setTab={setTab} />}
        {activeTab === 1 && isSuperAdmin && <UsersTab isSuperAdmin={isSuperAdmin} />}
        {activeTab === 2 && <RoleApplicationsTab />}
        {activeTab === 3 && <EventsTab isSuperAdmin={isSuperAdmin} userId={user?.id} />}
        {activeTab === 4 && features.shop && <ShopTab showOrders={!!features.shop_orders} />}
        {activeTab === 9 && <PartnersTab />}
        {activeTab === 10 && isSuperAdmin && <MembershipsTab />}
        {activeTab === 5 && <RegistrationsTab />}
        {activeTab === 6 && <ApprovalsTab isSuperAdmin={isSuperAdmin} />}
        {activeTab === 7 && <AdminBookingsRoot />}
        {activeTab === 8 && <AdminBroadcastTab isSuperAdmin={isSuperAdmin} />}
        {activeTab === 11 && <AdminReviewsTab />}
      </main>
    </div>
  );
}
